import { useState, useCallback } from "react";
import * as api from "../api";

// Las vacaciones de todo el equipo son visibles para todos; el permiso de
// escritura lo resuelve el backend y llega en cada fila como `can_edit`.
export function useVacaciones(token, showToast) {
  const [vacaciones, setVacaciones] = useState([]);

  const loadVacaciones = useCallback(async (from, to) => {
    if (!token) return;
    try {
      const data = await api.getVacaciones(from, to);
      if (Array.isArray(data)) setVacaciones(data);
    } catch (e) {
      showToast("Error al cargar las vacaciones", "error");
    }
  }, [token]); // eslint-disable-line

  // Refresca contra Odoo ANTES de listar, para que el calendario enseñe lo que
  // hay aprobado ahora mismo y no lo que se importó de madrugada.
  //
  // Se callan SOLO los dos casos en los que no hay nada que hacer y que le
  // pasan a mucha gente cada vez que abre la pantalla: no tener la cuenta de
  // Odoo conectada y tenerla caducada. Todo lo demás avisa.
  //
  // Al principio esto se tragaba cualquier error, y salió caro: la
  // sincronización estuvo dos rondas muriendo —primero por permisos de Odoo,
  // después por un tipo mal deducido en el SQL— y en pantalla se veía como
  // "no ha cambiado nada", que es indistinguible de que no hubiera nada que
  // cambiar. Un fallo que no se ve no se arregla.
  const sincronizarYCargar = useCallback(async (from, to) => {
    if (!token) return;
    try {
      await api.sincronizarVacacionesOdoo(from, to);
    } catch (e) {
      if (e.code !== "SIN_CONEXION" && e.code !== "SESION") {
        showToast(`No se pudo actualizar desde Odoo: ${e.message}`, "warn");
      }
    }
    await loadVacaciones(from, to);
  }, [token, loadVacaciones]); // eslint-disable-line

  const createVacacion = async (d) => {
    try {
      const v = await api.createVacacion(d);
      setVacaciones(p => [...p, v]);
      showToast("Periodo registrado");
      return v;
    } catch (e) { showToast(e.message || "Error al registrar el periodo", "error"); }
  };

  const updateVacacion = async (id, d) => {
    try {
      const v = await api.updateVacacion(id, d);
      setVacaciones(p => p.map(x => String(x.id) === String(id) ? { ...x, ...v } : x));
      showToast("Periodo actualizado");
      return v;
    } catch (e) { showToast(e.message || "Error al actualizar el periodo", "error"); }
  };

  // El backend ya ha devuelto la fila entera (p.ej. al solicitarla a RRHH):
  // aqui solo se refleja, sin volver a escribir.
  const reemplazarVacacion = (v) =>
    setVacaciones(p => p.map(x => String(x.id) === String(v.id) ? { ...x, ...v } : x));

  const deleteVacacion = async (id) => {
    try {
      await api.deleteVacacion(id);
      setVacaciones(p => p.filter(x => String(x.id) !== String(id)));
      showToast("Periodo eliminado", "warn");
    } catch (e) { showToast(e.message || "Error al eliminar el periodo", "error"); }
  };

  const importarVacaciones = async (text, mapa, comprobar) => {
    try {
      const r = await api.importarVacaciones(text, mapa, comprobar);
      if (!comprobar && !r.comprobado) {
        await loadVacaciones();
        showToast(`${r.importados} periodo(s) importado(s)${r.omitidos ? ` · ${r.omitidos} ya existia(n)` : ""}`);
      }
      return r;
    } catch (e) { showToast(e.message || "Error al importar los periodos", "error"); return null; }
  };

  return { vacaciones, setVacaciones, loadVacaciones, sincronizarYCargar, createVacacion, updateVacacion,
           reemplazarVacacion, deleteVacacion, importarVacaciones };
}
